import {
  useSubaccounts,
  ISubaccounts,
} from "../../hooks/api/admin/useSubaccounts";

const address = /^0x[0-9a-fA-F]{40}$/;
function matches(
  data: ISubaccounts | undefined,
  account: string
): data is ISubaccounts {
  return (
    !!data &&
    typeof data.account === "string" &&
    data.account.toLowerCase() === account.toLowerCase() &&
    (!data.parent || address.test(data.parent)) &&
    Array.isArray(data.subaccounts) &&
    data.subaccounts.every((item) => item && address.test(item.subaccount))
  );
}

/** Display only authoritative family links; proposed wallets never establish ownership. */
export function ApplicantAccount({
  account,
  proposedWallets = [],
}: {
  account: string;
  proposedWallets?: string[];
}) {
  const own = useSubaccounts(account, { poll: false });
  const ownValid = matches(own.data, account);
  const parent = ownValid ? own.data?.parent : undefined;
  const family = useSubaccounts(parent, { poll: false });
  const data = parent ? family.data : own.data;
  const failed =
    !!own.error ||
    !!(parent && family.error) ||
    (!!own.data && !ownValid) ||
    (!!data && !matches(data, parent || account));
  const loaded = ownValid && matches(data, parent || account);
  const proposals = proposedWallets.filter(
    (wallet) => wallet.toLowerCase() !== account.toLowerCase()
  );
  let familyDetails = <p>Loading linked accounts…</p>;
  if (failed) {
    familyDetails = (
      <p>
        Account links could not be loaded. The backend verifies the account
        family when you activate.
      </p>
    );
  } else if (loaded && data) {
    familyDetails = data.subaccounts.length ? (
      <ul>
        {data.subaccounts.map((member) => (
          <li key={member.subaccount} style={{ overflowWrap: "anywhere" }}>
            {member.name ? `${member.name} · ` : ""}
            {member.subaccount}
            {member.archived ? " · Archived" : ""}
          </li>
        ))}
      </ul>
    ) : (
      <p>No linked sub-accounts.</p>
    );
  }
  return (
    <div className="maker-form-wide">
      <label htmlFor="maker-applicant-account">
        Applicant account
        <input
          id="maker-applicant-account"
          aria-label="Applicant account"
          value={account}
          readOnly
          style={{ width: "100%" }}
        />
      </label>
      <p className="maker-field-help">
        The account used to submit this application. Activation covers its Aevo
        parent account and linked sub-accounts automatically.
      </p>
      {parent && (
        <p>
          Parent account:{" "}
          <span style={{ overflowWrap: "anywhere" }}>{parent}</span>
        </p>
      )}
      <strong>Linked sub-accounts</strong>
      {familyDetails}
      {proposals.length > 0 && (
        <div className="maker-form-note" role="note">
          <strong>This application listed additional wallets</strong>
          <p style={{ overflowWrap: "anywhere" }}>{proposals.join(", ")}</p>
          <p>
            Only the applicant account’s verified Aevo family will be enrolled.
            To use a separate account, the maker must sign into that account and
            apply again.
          </p>
        </div>
      )}
    </div>
  );
}
